import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { Ticket } from "@/types";
import { ScannerSimulation } from "@/components/ScannerSimulation";
import { VerificationStatus } from "@/components/VerificationStatus";
import { EntryLogs } from "@/components/EntryLogs";

const ClubEntry = () => {
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [recentEntries, setRecentEntries] = useState<Ticket[]>([]);

  const handleTicketScan = (ticket: Ticket) => {
    setSelectedTicket(ticket);
  };

  const handleConfirmEntry = (ticketId: string) => {
    if (selectedTicket) {
      setRecentEntries(prev => [selectedTicket, ...prev].slice(0, 5));
      // In a real app, this would call an API to update the ticket status
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white"
            asChild
          >
            <Link to="/security">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Security Portal
            </Link>
          </Button>
        </div>

        <div className="flex items-center gap-4 mb-8">
          <Shield className="h-8 w-8 text-purple-400" />
          <h1 className="text-3xl font-bold">Security Check-In Portal</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <Card className="bg-gray-900/50 border-gray-800 col-span-1">
            <CardHeader>
              <CardTitle>Scan Ticket</CardTitle>
            </CardHeader>
            <CardContent>
              <ScannerSimulation onScan={handleTicketScan} />
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-gray-800 col-span-1">
            <CardHeader>
              <CardTitle>Verification Status</CardTitle>
            </CardHeader>
            <CardContent>
              <VerificationStatus 
                ticket={selectedTicket} 
                onConfirm={handleConfirmEntry} 
              />
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-gray-800 col-span-1">
            <CardHeader>
              <CardTitle>Recent Entries</CardTitle>
            </CardHeader>
            <CardContent>
              <EntryLogs 
                recentEntries={recentEntries} 
              />
            </CardContent>
          </Card>
        </div>

        <Separator className="my-12 bg-gray-800" />

        <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <h3 className="font-medium">1. Scan Patron's QR Code</h3>
              <p className="text-gray-400">Use the scanner to verify their digital ticket and ID status.</p>
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">2. Review Verification</h3>
              <p className="text-gray-400">Check that the patron's ID has been verified and matches their profile.</p>
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">3. Confirm Entry</h3>
              <p className="text-gray-400">Allow or deny entry based on verification status and any alerts.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="h-3 w-3 bg-green-500 rounded-full"></span>
                System Status: Online
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p>Entry system is operational and ready for patron verification.</p>
              <p className="text-gray-400 mt-2 text-sm">Last update: {new Date().toLocaleTimeString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <CardTitle>Connect Security Earpiece</CardTitle>
            </CardHeader>
            <CardContent>
              <Button className="w-full" variant="outline">
                Connect Device
              </Button>
              <p className="text-gray-400 mt-2 text-sm">Connect your security earpiece for team communications</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClubEntry;
